import type {ObjectFieldsAssertion} from '@wix/devzai-utils-common';
import {arrayRemoveNullValues, objectRemoveEntriesWithUndefinedValues} from '@wix/devzai-utils-common';
import type {WixBlogCoverMedia, WixBlogImage, WixBlogPageUrl, WixImageInfo} from '@wix/devzai-common-wix';


export interface DoppeDtoBlogPost {
    id: string;
    title: string;
    excerpt: string;
    media: WixBlogCoverMedia | null;
    blogPostPageUrl?: string;
    firstPublishedDate: Date | null;
    minutestToRead: number | null;
    slug?: string;
}

export namespace DoppeDtoBlogPost {
    export type Id = DoppeDtoBlogPost['id'];

    export type ForUpdate = Pick<DoppeDtoBlogPost, 'id' | 'title'>

    export type ForCreation = Pick<DoppeDtoBlogPost, 'title'>
}


export function doppeBlogPostResolveUrl(blogPostPageUrl: WixBlogPageUrl) {
    if (blogPostPageUrl?.base && blogPostPageUrl?.path) {
        return `${blogPostPageUrl.base}${blogPostPageUrl.path}`;
    }

    return undefined;
}

export function doppeBlogPostResolveSlug(blogPost: DoppeDtoBlogPost) {
    return blogPost.slug
}

export const DoppeDtoBlogPostForCreationFieldsAssertions: ObjectFieldsAssertion<DoppeDtoBlogPost.ForCreation> = {
    title: assert => assert.isString(),
}

export const DoppeDtoBlogPostForUpdateFieldsAssertions: ObjectFieldsAssertion<DoppeDtoBlogPost.ForUpdate> = {
    id: assert => assert.isString(),
    title: assert => assert.isString(),
}

export function convertWixBlogCommonImageToWixImageInfo(
    wixBlogCommonImage: WixBlogImage
): WixImageInfo {
    return objectRemoveEntriesWithUndefinedValues({
        url: wixBlogCommonImage.url,
        title: wixBlogCommonImage.altText ?? undefined,
        width: wixBlogCommonImage.width,
        height: wixBlogCommonImage.height,
    })
}

export function doppeDtoBlogPostGetMedia (blogPost: DoppeDtoBlogPost) {
    return arrayRemoveNullValues([
        blogPost.media?.image ? convertWixBlogCommonImageToWixImageInfo(blogPost.media.image) : null,
        blogPost.media?.video?.thumbnail ? convertWixBlogCommonImageToWixImageInfo(blogPost.media.video.thumbnail) : null
    ])
}

/**
 * Options for creating a dummy blog post with localized content and images.
 */
export interface DoppeDtoBlogPostCreateDummyOptions {
    /**
     * Localized blog post title.
     */
    title: string;
    /**
     * Localized blog post excerpt.
     */
    excerpt: string;
    /**
     * Blog post image (WixImageInfo format).
     */
    image: WixImageInfo | null;
}

/**
 * Creates a dummy blog post with placeholder content.
 * Used when no actual blog post is available (e.g., for preview or empty state).
 */
export function doppeDtoBlogPostCreateDummy(
    options: DoppeDtoBlogPostCreateDummyOptions
): DoppeDtoBlogPost {
    const blogImage: WixBlogCoverMedia | null = options.image ? {
        image: {
            url: options.image.url,
            width: options.image.width,
            height: options.image.height
        },
        enabled: true,
        displayed: true
    } : null;

    return {
        id: 'dummy-blog-post',
        title: options.title,
        excerpt: options.excerpt,
        media: blogImage,
        firstPublishedDate: null,
        minutestToRead: 5,
        slug: 'dummy-blog-post'
    };
}
